import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useRemembered } from '../lib/remember';
import { headingCount } from '../lib/libraryStates';
import { WorthRing } from './bits';

// A page's folding sections (My books › Library copies and Add & import; the check-up's fold rule):
// each fold remembered on this device, one Expand all / Collapse all for the page (SectionFoldAll,
// in the pinned bar), and a way to open one from a link that leads to it (openSectionFold, F11).

const folds = new Map<string, { open: boolean; set: (v: boolean) => void }>();
const watchers = new Set<() => void>();
const changed = () => watchers.forEach((w) => w());

/** Opens one section by its id (arriving from a link or a chip that leads to it, filters review F11). */
export function openSectionFold(id: string) {
  const f = folds.get(id);
  if (f && !f.open) f.set(true);
}

/** A section that folds, remembered on this device under `${store}:${id}`. */
export function SectionFold({
  store,
  id,
  title,
  count,
  noun,
  note,
  small,
  closed,
  worth,
  end,
  children,
}: {
  /** Where its fold is remembered ("my_books:library"). */
  store: string;
  id: string;
  title: string;
  /** The heading's count, in words ("On loan · 3 books"); none when 0 or missing. */
  count?: number;
  /** What the count counts (books, unless said). */
  noun?: [string, string];
  note?: string;
  /** A fold inside a section: smaller, and not counted by Expand all. */
  small?: boolean;
  closed?: boolean;
  /** Worth a look (filters review F2): the ring on the heading, with its tip. */
  worth?: string;
  /** Something at the right of the heading (Recently added's When). */
  end?: ComponentChildren;
  children: ComponentChildren;
}) {
  const [open, setOpen] = useRemembered<boolean>(`${store}:${id}`, !closed);
  useEffect(() => {
    if (small) return;
    folds.set(id, { open, set: setOpen });
    changed();
  });
  useEffect(
    () => () => {
      folds.delete(id);
      changed();
    },
    [id],
  );
  const H = small ? 'h3' : 'h2';
  return (
    <section class={`mb-fold${small ? ' small' : ''}${end ? ' has-end' : ''}`} aria-label={title} data-fold={id}>
      <H class="mb-fold-head">
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span aria-hidden="true">{open ? '▾' : '▸'}</span> {worth ? <WorthRing tip={worth} /> : null}
          {title}
          {/* The count in words (R7 N4, Kristi Nov 2): "On loan · 3 books"; an empty section shows none. */}
          {count ? <span class="heading-count"> · {headingCount(count, noun)}</span> : null}
        </button>
        {note ? <span class="mb-fold-note">{note}</span> : null}
      </H>
      {/* Outside the heading, so the heading's name is just its title and count. */}
      {end ? <span class="mb-fold-end">{end}</span> : null}
      {open ? <div class="mb-fold-body">{children}</div> : null}
    </section>
  );
}

/** Collapse all while any section is open, Expand all once they're all folded (as Shelves). */
export function SectionFoldAll() {
  const [, tick] = useState(0);
  useEffect(() => {
    const w = () => tick((n) => n + 1);
    watchers.add(w);
    return () => void watchers.delete(w);
  }, []);
  const all = [...folds.values()];
  if (!all.length) return null;
  const anyOpen = all.some((f) => f.open);
  return (
    <button type="button" class="btn-link mb-foldall" onClick={() => all.forEach((f) => f.set(!anyOpen))}>
      {anyOpen ? 'Collapse all' : 'Expand all'}
    </button>
  );
}
